import { getJson, sendJson } from '@/lib/api';

/**
 * The sales dashboard (Analytics → Sales): typed fetchers over /api/v3/sales.
 * Shapes mirror apps/api's sales-metrics, sales-queue and sales-rep. Every
 * definition (won stages, win rates, pipeline estimate, stale) is computed
 * server-side to match hq's sales scorecard; the pages only display.
 */

export interface MonthlyBookings {
  readonly month: string;
  readonly revenue: number;
  readonly bookings: number;
}

/** One window's scorecard (mirrors the API's PeriodScore). */
export interface PeriodScore {
  readonly revenue: number;
  readonly bookings: number;
  readonly lost: number;
  readonly shouldClose: number;
  readonly winRate: number | null;
  readonly adjustedWinRate: number | null;
  readonly medianDaysToSign: number | null;
  readonly averageDealValue: number | null;
}

export interface OpenScore {
  readonly open: number;
  readonly priced: number;
  readonly flagged: number;
  readonly pipelineEstimate: number;
  readonly stale: number;
  readonly pastClose: number;
  readonly shouldClose: number;
}

export interface FirstTouchScore {
  readonly newOpportunities: number;
  readonly touchedWithin24h: number;
  readonly share: number | null;
  readonly averageHours: number | null;
  readonly medianHours: number | null;
  readonly untouched: number;
}

/** Whose business a deal is (mirrors the API's CUSTOMER_TYPE): repeat when the account signed before. */
export const CUSTOMER_TYPE = {
  new: 'new',
  repeat: 'repeat',
} as const;
export type CustomerType = (typeof CUSTOMER_TYPE)[keyof typeof CUSTOMER_TYPE];
export const CUSTOMER_TYPES: readonly CustomerType[] = [CUSTOMER_TYPE.new, CUSTOMER_TYPE.repeat];
export const CUSTOMER_TYPE_LABELS: Readonly<Record<CustomerType, string>> = {
  [CUSTOMER_TYPE.new]: 'New customers',
  [CUSTOMER_TYPE.repeat]: 'Repeat customers',
};

/** One customer type's share of a rep's or the team's scorecard. */
export interface CustomerTypeScore {
  readonly period: PeriodScore;
  readonly previous: PeriodScore;
  readonly open: OpenScore;
  readonly firstTouch: FirstTouchScore;
}

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
/** Past this many hours a first-touch time reads in days. */
const HOURS_SHOWN_UP_TO = 48;

/**
 * A duration in hours as people say it: minutes under an hour, hours up to two
 * days, then days.
 *
 * @param hours - The duration, or null when there is none.
 */
export function hoursLabel(hours: number | null): string {
  if (hours === null) return 'n/a';
  if (hours < 1) return `${Math.round(hours * MINUTES_PER_HOUR)} min`;
  if (hours <= HOURS_SHOWN_UP_TO) return `${hours.toFixed(1)} hrs`;
  return `${(hours / HOURS_PER_DAY).toFixed(1)} days`;
}

/** Loss-reason groups, as the API groups Reason_Lost__c (sales/loss-reasons.ts). */
export const LOSS_GROUP = {
  noResponse: 'no_response',
  budget: 'budget',
  choseOther: 'chose_other',
  competitor: 'competitor',
  cancelled: 'cancelled',
  other: 'other',
} as const;
export type LossGroup = (typeof LOSS_GROUP)[keyof typeof LOSS_GROUP];

/** Chart order and labels. */
export const LOSS_GROUP_LABELS: Readonly<Record<LossGroup, string>> = {
  [LOSS_GROUP.noResponse]: 'No response',
  [LOSS_GROUP.budget]: 'Budget or price',
  [LOSS_GROUP.choseOther]: 'Chose something else',
  [LOSS_GROUP.competitor]: 'Lost to a competitor',
  [LOSS_GROUP.cancelled]: 'Canceled',
  [LOSS_GROUP.other]: 'Other or not given',
};

export interface MonthlyLosses {
  readonly month: string;
  readonly total: number;
  readonly byGroup: Readonly<Record<LossGroup, number>>;
}

export interface LossReasonCount {
  readonly reason: string;
  readonly group: LossGroup;
  readonly count: number;
}

export interface LossScore {
  readonly trend: readonly MonthlyLosses[];
  readonly reasons: readonly LossReasonCount[];
}

export interface RepScore {
  readonly ownerName: string;
  readonly ownerId: string;
  /** The selected timeframe. */
  readonly period: PeriodScore;
  /** The timeframe it is compared with. */
  readonly previous: PeriodScore;
  readonly open: OpenScore;
  readonly firstTouch: FirstTouchScore;
  readonly trend: readonly MonthlyBookings[];
  readonly losses: LossScore;
  /** The same figures split by new and repeat customers. */
  readonly byCustomerType: Readonly<Record<CustomerType, CustomerTypeScore>>;
}

export interface TeamScore {
  readonly period: PeriodScore;
  readonly previous: PeriodScore;
  readonly threeMonthAverage: number;
  readonly monthlyGoal: number;
  /** The $10M-a-year pace for the timeframe's days. */
  readonly periodGoal: number;
  readonly open: OpenScore;
  readonly shouldCloseAllOwners: number;
  readonly firstTouch: FirstTouchScore;
  readonly trend: readonly MonthlyBookings[];
  readonly otherTrend: readonly MonthlyBookings[];
  readonly losses: LossScore;
  /** The same figures split by new and repeat customers. */
  readonly byCustomerType: Readonly<Record<CustomerType, CustomerTypeScore>>;
}

/** The scorecard's timeframe presets, as they appear in the URL (mirrors the API's SALES_RANGE). */
export const SALES_RANGE = {
  thisMonth: 'month',
  lastMonth: 'last-month',
  last30Days: '30d',
  last90Days: '90d',
  thisQuarter: 'quarter',
  lastQuarter: 'last-quarter',
  yearToDate: 'ytd',
  last12Months: '12m',
  custom: 'custom',
} as const;
export type SalesRange = (typeof SALES_RANGE)[keyof typeof SALES_RANGE];

/** Every range but custom: resolved on the server from the clock alone. */
export type SalesPreset = Exclude<SalesRange, typeof SALES_RANGE.custom>;

/** Enough resolved deals per rep for a stable win rate. */
export const DEFAULT_SALES_RANGE: SalesPreset = SALES_RANGE.last90Days;

/** Picker order and labels; `shortLabel` is what a phone shows. */
export const SALES_RANGE_OPTIONS: ReadonlyArray<{ readonly key: SalesRange; readonly label: string; readonly shortLabel: string }> = [
  { key: SALES_RANGE.thisMonth, label: 'This month', shortLabel: 'MTD' },
  { key: SALES_RANGE.lastMonth, label: 'Last month', shortLabel: 'Last mo' },
  { key: SALES_RANGE.last30Days, label: 'Last 30 days', shortLabel: '30d' },
  { key: SALES_RANGE.last90Days, label: 'Last 90 days', shortLabel: '90d' },
  { key: SALES_RANGE.thisQuarter, label: 'This quarter', shortLabel: 'QTD' },
  { key: SALES_RANGE.lastQuarter, label: 'Last quarter', shortLabel: 'Last qtr' },
  { key: SALES_RANGE.yearToDate, label: 'Year to date', shortLabel: 'YTD' },
  { key: SALES_RANGE.last12Months, label: 'Last 12 months', shortLabel: '1y' },
  { key: SALES_RANGE.custom, label: 'Custom', shortLabel: 'Custom' },
];

/** The URL keys the timeframe lives under (the profit section has its own). */
export const SALES_TIMEFRAME_PARAM = { range: 'range', from: 'from', to: 'to' } as const;

/** No custom window starts before this day (the API's SALES_WINDOW_LIMITS.earliest). */
export const SALES_EARLIEST_DAY = '2018-01-01';

/** What the scorecard is asked for: a preset, or custom with both days (inclusive). */
export type SalesTimeframe =
  | { readonly range: SalesPreset }
  | { readonly range: typeof SALES_RANGE.custom; readonly from: string; readonly to: string };

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isSalesRange(value: string | null): value is SalesRange {
  return value !== null && (Object.values(SALES_RANGE) as string[]).includes(value);
}

/**
 * The timeframe in a URL: `?range=` (default Last 90 days), with `from` and
 * `to` for custom. A custom range missing a valid day falls back to the
 * default rather than asking the API for a window it would refuse.
 *
 * @param params - The page's search params.
 */
export function parseSalesTimeframe(params: URLSearchParams): SalesTimeframe {
  const range = params.get(SALES_TIMEFRAME_PARAM.range);
  if (!isSalesRange(range)) return { range: DEFAULT_SALES_RANGE };
  if (range !== SALES_RANGE.custom) return { range };
  const from = params.get(SALES_TIMEFRAME_PARAM.from) ?? '';
  const to = params.get(SALES_TIMEFRAME_PARAM.to) ?? '';
  if (!DAY_PATTERN.test(from) || !DAY_PATTERN.test(to) || from > to) {
    return { range: DEFAULT_SALES_RANGE };
  }
  return { range, from, to };
}

/**
 * The timeframe as query params, for the API and for links that carry it.
 * The default is left out so a plain URL stays plain.
 */
export function salesTimeframeParams(timeframe: SalesTimeframe): Record<string, string> {
  if (timeframe.range === SALES_RANGE.custom) {
    return { range: timeframe.range, from: timeframe.from, to: timeframe.to };
  }
  return timeframe.range === DEFAULT_SALES_RANGE ? {} : { range: timeframe.range };
}

/** `?range=…` for a link that keeps the timeframe, or '' for the default. */
export function salesTimeframeSearch(timeframe: SalesTimeframe): string {
  const search = new URLSearchParams(salesTimeframeParams(timeframe)).toString();
  return search ? `?${search}` : '';
}

/** A resolved window, both days inclusive (mirrors the API's SalesWindow). */
export interface SalesWindow {
  readonly from: string;
  readonly to: string;
  readonly days: number;
  /** "Last 90 days", "October to date"; for the previous window it reads after an amount ("previous 90 days"). */
  readonly label: string;
}

export interface SalesScorecard {
  readonly range: SalesRange;
  readonly window: SalesWindow;
  readonly previousWindow: SalesWindow;
  readonly winRateTarget: number;
  readonly team: TeamScore;
  readonly reps: readonly RepScore[];
  readonly generatedAt: string;
  readonly dataAsOf: string | null;
}

/** The rep page's segments (mirrors the API's REP_SEGMENT). */
export const REP_SEGMENT = {
  open: 'open',
  won: 'won',
  lost: 'lost',
  unqualified: 'unqualified',
} as const;
export type RepSegment = (typeof REP_SEGMENT)[keyof typeof REP_SEGMENT];

/** Where a rep-page value comes from (mirrors the API's VALUE_KIND). */
export const VALUE_KIND = {
  contract: 'contract',
  estimate: 'estimate',
  none: 'none',
} as const;
export type ValueKind = (typeof VALUE_KIND)[keyof typeof VALUE_KIND];

/** Sortable rep-page columns (mirrors the API's REP_SORT_FIELDS keys). */
export const REP_SORT_KEYS = ['name', 'stage', 'closeDate', 'value', 'touch', 'stageChange', 'created'] as const;

export interface RepLead {
  readonly leadId: string;
  readonly name: string;
  readonly company: string;
  readonly channel: string;
  readonly createdAt: string;
}

export interface RepOpportunityRow {
  readonly opportunityId: string;
  readonly name: string;
  readonly accountName: string;
  readonly stageName: string;
  readonly segment: RepSegment;
  readonly createdAt: string;
  readonly closeDate: string | null;
  readonly value: number | null;
  readonly valueKind: ValueKind;
  readonly flagged: boolean;
  readonly lastTouchAt: string | null;
  readonly daysSinceTouch: number | null;
  readonly daysSinceStageChange: number;
  readonly leads: readonly RepLead[];
}

export interface RepOpportunities {
  readonly owner: { readonly ownerId: string; readonly ownerName: string };
  readonly rows: readonly RepOpportunityRow[];
  readonly counts: Readonly<Record<RepSegment, number>>;
  readonly total: number;
  readonly page: number;
  readonly totalPages: number;
}

/** The review queue's tabs (mirrors the API's QUEUE_TAB). */
export const QUEUE_TAB = {
  shouldClose: 'should_close',
  goingStale: 'going_stale',
} as const;
export type QueueTab = (typeof QUEUE_TAB)[keyof typeof QUEUE_TAB];

/** Why a row is in the queue (mirrors the API's SHOULD_CLOSE_REASON and GOING_STALE_REASON). */
export const QUEUE_REASON = {
  eventDatePassed: 'event_date_passed',
  closeDatePassed: 'close_date_passed',
  noActivity: 'no_activity',
  pastCloseDate: 'past_close_date',
} as const;
export type QueueReason = (typeof QUEUE_REASON)[keyof typeof QUEUE_REASON];

export interface SalesQueueRow {
  readonly opportunityId: string;
  readonly name: string;
  readonly accountName: string;
  readonly ownerId: string;
  readonly ownerName: string;
  readonly stageName: string;
  readonly tab: QueueTab;
  readonly reason: QueueReason;
  readonly diedAt: string | null;
  readonly estimate: number | null;
  readonly flagged: boolean;
  readonly lastTouchAt: string | null;
  readonly lastTouchSubject: string;
  readonly lastTouchSubtype: string;
  readonly stageChangedAt: string;
  readonly closeDate: string | null;
  readonly eventDate: string | null;
  readonly idleDays: number;
}

export interface SalesQueue {
  readonly shouldClose: readonly SalesQueueRow[];
  readonly goingStale: readonly SalesQueueRow[];
  readonly snoozed: number;
  readonly owners: ReadonlyArray<{ readonly ownerId: string; readonly ownerName: string }>;
  readonly roster: readonly string[];
  readonly lossReasons: readonly string[];
}

/** Queue actions (mirrors the API's SALES_ACTION). */
export const SALES_ACTION = {
  closeLost: 'close_lost',
  closeUnqualified: 'close_unqualified',
  snooze: 'snooze',
} as const;
export type SalesAction = (typeof SALES_ACTION)[keyof typeof SALES_ACTION];

export interface SalesActionResult {
  readonly action: SalesAction;
  readonly appliedIds: readonly string[];
  readonly failures: ReadonlyArray<{ readonly opportunityId: string; readonly reason: string }>;
  readonly snoozedUntil: string | null;
}

/** Query keys, named once so a queue action can refresh every sales view. */
export const SALES_QUERY_KEY = {
  root: ['sales'],
  scorecard: (timeframe: SalesTimeframe) => ['sales', 'scorecard', timeframe] as const,
  rep: (ownerId: string, params: Record<string, unknown>) => ['sales', 'rep', ownerId, params] as const,
  queue: (ownerId: string | undefined) => ['sales', 'queue', ownerId ?? 'all'] as const,
} as const;

export { salesforceOpportunityUrl } from './salesforce';

export function readSalesScorecard(timeframe: SalesTimeframe, signal?: AbortSignal): Promise<SalesScorecard> {
  const params = salesTimeframeParams(timeframe);
  return getJson<SalesScorecard>('/sales/scorecard', Object.keys(params).length > 0 ? params : undefined, signal);
}

export function readRepOpportunities(
  ownerId: string,
  params: {
    segment: RepSegment;
    q?: string;
    page: number;
    sort?: string;
    dir?: 'asc' | 'desc';
  },
  signal?: AbortSignal,
): Promise<RepOpportunities> {
  return getJson<RepOpportunities>(`/sales/reps/${encodeURIComponent(ownerId)}/opportunities`, params, signal);
}

export function readSalesQueue(ownerId: string | undefined, signal?: AbortSignal): Promise<SalesQueue> {
  return getJson<SalesQueue>('/sales/queue', ownerId ? { ownerId } : undefined, signal);
}

export function runSalesAction(body: {
  action: SalesAction;
  opportunityIds: readonly string[];
  lossReason?: string;
}): Promise<SalesActionResult> {
  return sendJson<SalesActionResult>('post', '/sales/queue/actions', body);
}

// ─── Display helpers ─────────────────────────────────────────────────────

const shortMonthFormat = new Intl.DateTimeFormat('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });

/** "2026-09" → "Sep 26", for chart axes. */
export function shortMonth(month: string): string {
  return shortMonthFormat.format(new Date(`${month}-01T00:00:00.000Z`));
}

/** A rate as "25.0%", or "n/a" when nothing was decided. */
export function percent(rate: number | null): string {
  return rate === null ? 'n/a' : `${(rate * 100).toFixed(1)}%`;
}

/** Compact dollars for tiles and charts: $2.8M, $457k. */
export function compactMoney(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/** A calendar date ("2026-09-12" or an ISO instant) as "Sep 12, 2026". */
export function dateLabel(value: string | null): string {
  if (value === null) return 'None';
  return new Date(value.length === 10 ? `${value}T00:00:00.000Z` : value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** How long ago, in days: "Today", "1 day ago", "12 days ago". */
export function daysAgoLabel(days: number): string {
  return days === 0 ? 'Today' : `${daysLabel(days)} ago`;
}

/** Days as "1 day" / "12 days". */
export function daysLabel(days: number): string {
  return days === 1 ? '1 day' : `${days.toLocaleString('en-US')} days`;
}

/**
 * Why a row is in the queue, in the reader's words. "No activity" means 60
 * days on the should-be-closed tab and 30 on the going-stale tab.
 */
export function queueReasonLabel(reason: QueueReason, tab: QueueTab): string {
  switch (reason) {
    case QUEUE_REASON.eventDatePassed:
      return 'Event date passed';
    case QUEUE_REASON.closeDatePassed:
      return '30+ days past close';
    case QUEUE_REASON.pastCloseDate:
      return 'Past close date';
    case QUEUE_REASON.noActivity:
      return tab === QUEUE_TAB.shouldClose ? 'Idle 60+ days' : 'Idle 30+ days';
    default: {
      const unreachable: never = reason;
      return unreachable;
    }
  }
}
